import { useMemo, useState } from "react";
import { Navigate } from "react-router-dom";
import ReactECharts from "echarts-for-react";

import { useMe } from "../api/me";
import {
  useTelemetryOutliers,
  useTelemetryReanalyze,
  useTelemetrySummary,
  useTelemetryServices,
  useTelemetryTiers,
} from "../api/telemetry";
import { echarts, PALETTE, baseAxis, baseTooltip } from "../charts/echarts";
import FeedbackPanel from "../components/feedback/FeedbackPanel";
import Footer from "../components/Footer";
import PageHeader from "../components/PageHeader";
import TopNav from "../components/TopNav";
import { isAdmin } from "../lib/admin";

type TopTab = "usage" | "feedback";

type Granularity = "day" | "week";

const RANGES: { id: number; label: string }[] = [
  { id: 7, label: "7d" },
  { id: 30, label: "30d" },
  { id: 90, label: "90d" },
];

const SERVICE_COLORS: Record<string, string> = {
  llm: "#38bdf8",
  yfinance: "#34d399",
  marketaux: "#fbbf24",
  rss: "#a78bfa",
  ark: "#fb7185",
  insider: "#22d3ee",
  telegram: "#f97316",
  apewisdom: "#f472b6",
  stocktwits: "#84cc16",
};
const SERVICE_FALLBACK = "#94a3b8";
const colorFor = (svc: string) => SERVICE_COLORS[svc] ?? SERVICE_FALLBACK;

function fmtUSD(micros: number): string {
  const usd = micros / 1_000_000;
  if (usd === 0) return "$0.00";
  if (Math.abs(usd) < 0.01) return `<$0.01`;
  return `$${usd.toFixed(2)}`;
}

function fmtCompact(n: number): string {
  if (n >= 1_000_000_000) return `${(n / 1e9).toFixed(1)}B`;
  if (n >= 1_000_000) return `${(n / 1e6).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1e3).toFixed(1)}K`;
  return String(n);
}

// Toggle one service in a multiselect. Empty selection = "all".
function toggle(set: string[], s: string): string[] {
  return set.includes(s) ? set.filter((x) => x !== s) : [...set, s];
}

function Card({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="rounded-2xl border border-slate-800 bg-slate-900 p-4">
      <div className="mb-3 text-xs font-medium uppercase tracking-wide text-slate-400">
        {title}
      </div>
      {children}
    </section>
  );
}

function Stat({ label, value, sub }: { label: string; value: string; sub?: string }) {
  return (
    <div className="rounded-xl border border-slate-800 bg-slate-950/40 p-3">
      <div className="text-[10px] uppercase tracking-wide text-slate-500">{label}</div>
      <div className="mt-1 font-mono text-xl tabular-nums text-slate-100">{value}</div>
      {sub && <div className="mt-0.5 text-[11px] text-slate-500">{sub}</div>}
    </div>
  );
}

export default function Telemetry() {
  const me = useMe();
  // Mirror the backend gate so a stray click on the URL bounces cleanly.
  if (me.isLoading) return <div className="p-8 text-slate-400">Loading…</div>;
  if (!isAdmin(me.data?.email)) return <Navigate to="/" replace />;

  return <TelemetryInner />;
}

function TelemetryInner() {
  const [tab, setTab] = useState<TopTab>("usage");
  const [days, setDays] = useState<number>(30);
  const [granularity, setGranularity] = useState<Granularity>("day");
  const [services, setServices] = useState<string[]>([]); // empty = all

  const servicesQ = useTelemetryServices();
  const allServices = servicesQ.data?.services ?? [];

  const summary = useTelemetrySummary(days, services, granularity);
  const tiers = useTelemetryTiers(days);
  const reanalyze = useTelemetryReanalyze(days);
  const outliers = useTelemetryOutliers(Math.min(days, 30));

  // ---- Stacked bar: calls per bucket per service ----
  const summaryOption = useMemo<echarts.EChartsCoreOption>(() => {
    const points = summary.data?.points ?? [];
    const dates = Array.from(new Set(points.map((p) => p.bucket))).sort();
    const seen = Array.from(new Set(points.map((p) => p.service))).sort();
    const series = seen.map((svc) => {
      const map = new Map<string, number>();
      for (const p of points) if (p.service === svc) map.set(p.bucket, p.calls);
      return {
        name: svc,
        type: "bar" as const,
        stack: "calls",
        emphasis: { focus: "series" as const },
        itemStyle: { color: colorFor(svc) },
        data: dates.map((d) => map.get(d) ?? 0),
      };
    });
    return {
      tooltip: { ...baseTooltip, axisPointer: { type: "shadow" } },
      legend: {
        data: seen, top: 0, textStyle: { color: PALETTE.muted, fontSize: 10 },
      },
      grid: { left: 36, right: 12, top: 32, bottom: 24 },
      xAxis: { ...baseAxis, type: "category", data: dates },
      yAxis: { ...baseAxis, type: "value", splitLine: { lineStyle: { color: PALETTE.grid } } },
      series,
    };
  }, [summary.data]);

  // ---- LLM tokens line (in vs out) ----
  const tokensOption = useMemo<echarts.EChartsCoreOption | null>(() => {
    const points = (summary.data?.points ?? []).filter((p) => p.service === "llm");
    if (!points.length) return null;
    const dates = Array.from(new Set(points.map((p) => p.bucket))).sort();
    const inMap = new Map(points.map((p) => [p.bucket, p.tokens_in]));
    const outMap = new Map(points.map((p) => [p.bucket, p.tokens_out]));
    return {
      tooltip: { ...baseTooltip },
      legend: { data: ["prompt", "completion"], top: 0, textStyle: { color: PALETTE.muted, fontSize: 10 } },
      grid: { left: 44, right: 12, top: 32, bottom: 24 },
      xAxis: { ...baseAxis, type: "category", data: dates },
      yAxis: {
        ...baseAxis, type: "value",
        axisLabel: { ...baseAxis.axisLabel, formatter: (v: number) => fmtCompact(v) },
      },
      series: [
        {
          name: "prompt", type: "line", smooth: true, showSymbol: false,
          itemStyle: { color: PALETTE.accent },
          areaStyle: { color: "rgba(56,189,248,0.15)" },
          data: dates.map((d) => inMap.get(d) ?? 0),
        },
        {
          name: "completion", type: "line", smooth: true, showSymbol: false,
          itemStyle: { color: "#fbbf24" },
          areaStyle: { color: "rgba(251,191,36,0.15)" },
          data: dates.map((d) => outMap.get(d) ?? 0),
        },
      ],
    };
  }, [summary.data]);

  // ---- Tier composition (per user, stacked bar) ----
  const tierByUser = useMemo(() => {
    const points = tiers.data?.points ?? [];
    const byUid = new Map<number, { email: string | null; rows: typeof points }>();
    for (const p of points) {
      const e = byUid.get(p.user_id) ?? { email: p.email, rows: [] };
      e.rows.push(p);
      byUid.set(p.user_id, e);
    }
    return Array.from(byUid.entries()).map(([uid, v]) => ({
      uid, email: v.email, rows: v.rows.sort((a, b) => a.date.localeCompare(b.date)),
    }));
  }, [tiers.data]);

  function tierOption(rows: { date: string; live: number; tracked: number; idle: number }[]): echarts.EChartsCoreOption {
    const dates = rows.map((r) => r.date);
    return {
      tooltip: { ...baseTooltip, axisPointer: { type: "shadow" } },
      legend: { data: ["live", "tracked", "idle"], top: 0, textStyle: { color: PALETTE.muted, fontSize: 10 } },
      grid: { left: 30, right: 8, top: 30, bottom: 24 },
      xAxis: { ...baseAxis, type: "category", data: dates },
      yAxis: { ...baseAxis, type: "value" },
      series: [
        { name: "live", type: "bar", stack: "tier",
          itemStyle: { color: PALETTE.accent }, data: rows.map((r) => r.live) },
        { name: "tracked", type: "bar", stack: "tier",
          itemStyle: { color: "#94a3b8" }, data: rows.map((r) => r.tracked) },
        { name: "idle", type: "bar", stack: "tier",
          itemStyle: { color: "#475569" }, data: rows.map((r) => r.idle) },
      ],
    };
  }

  // ---- Re-analyze per day, grouped per user ----
  const reanalyzeOption = useMemo<echarts.EChartsCoreOption | null>(() => {
    const points = reanalyze.data?.points ?? [];
    if (!points.length) return null;
    const dates = Array.from(new Set(points.map((p) => p.day))).sort();
    const userIds = Array.from(new Set(points.map((p) => p.user_id)));
    const labelByUid = new Map(points.map((p) => [p.user_id, p.email ?? `uid:${p.user_id}`]));
    const userColors = ["#38bdf8", "#fbbf24", "#a78bfa", "#34d399", "#fb7185"];
    const series = userIds.map((uid, i) => {
      const map = new Map<string, number>();
      for (const p of points) if (p.user_id === uid) map.set(p.day, p.count);
      return {
        name: labelByUid.get(uid) ?? `uid:${uid}`,
        type: "bar" as const,
        itemStyle: { color: userColors[i % userColors.length] },
        data: dates.map((d) => map.get(d) ?? 0),
      };
    });
    return {
      tooltip: { ...baseTooltip, axisPointer: { type: "shadow" } },
      legend: { top: 0, textStyle: { color: PALETTE.muted, fontSize: 10 } },
      grid: { left: 30, right: 8, top: 30, bottom: 24 },
      xAxis: { ...baseAxis, type: "category", data: dates },
      yAxis: { ...baseAxis, type: "value", minInterval: 1 },
      series,
    };
  }, [reanalyze.data]);

  // ---- Summary stats ----
  const totals = useMemo(() => {
    const pts = summary.data?.points ?? [];
    let calls = 0, tokensIn = 0, tokensOut = 0, cost = 0, errors = 0;
    for (const p of pts) {
      calls += p.calls;
      tokensIn += p.tokens_in;
      tokensOut += p.tokens_out;
      cost += p.cost_micros;
      errors += p.errors;
    }
    return { calls, tokensIn, tokensOut, cost, errors };
  }, [summary.data]);

  return (
    <div className="mx-auto max-w-5xl p-4 sm:p-6">
      <PageHeader />
      <TopNav />

      <header className="mb-4 flex flex-wrap items-baseline gap-x-3 gap-y-1">
        <h1 className="text-lg font-semibold text-slate-100">Telemetry</h1>
        <span className="text-[11px] uppercase tracking-wide text-slate-600">
          owner-only · usage + feedback
        </span>
      </header>

      <div className="mb-4 flex gap-1 rounded-full bg-slate-900 p-0.5 ring-1 ring-slate-800 w-fit">
        {(["usage", "feedback"] as TopTab[]).map((t) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            className={`rounded-full px-3 py-1 text-xs capitalize transition ${
              tab === t ? "bg-slate-800 text-slate-100" : "text-slate-400 hover:text-slate-200"
            }`}
          >
            {t}
          </button>
        ))}
      </div>

      {tab === "feedback" ? (
        <>
          <FeedbackPanel />
          <Footer />
        </>
      ) : (
        <UsageBody
          days={days}
          setDays={setDays}
          granularity={granularity}
          setGranularity={setGranularity}
          services={services}
          setServices={setServices}
          allServices={allServices}
          summary={summary}
          summaryOption={summaryOption}
          tokensOption={tokensOption}
          tiers={tiers}
          tierByUser={tierByUser}
          tierOption={tierOption}
          reanalyze={reanalyze}
          reanalyzeOption={reanalyzeOption}
          outliers={outliers}
          totals={totals}
        />
      )}
    </div>
  );
}

interface UsageBodyProps {
  days: number;
  setDays: (n: number) => void;
  granularity: Granularity;
  setGranularity: (g: Granularity) => void;
  services: string[];
  setServices: (s: string[]) => void;
  allServices: string[];
  summary: ReturnType<typeof useTelemetrySummary>;
  summaryOption: echarts.EChartsCoreOption;
  tokensOption: echarts.EChartsCoreOption | null;
  tiers: ReturnType<typeof useTelemetryTiers>;
  tierByUser: { uid: number; email: string | null; rows: { date: string; live: number; tracked: number; idle: number }[] }[];
  tierOption: (rows: { date: string; live: number; tracked: number; idle: number }[]) => echarts.EChartsCoreOption;
  reanalyze: ReturnType<typeof useTelemetryReanalyze>;
  reanalyzeOption: echarts.EChartsCoreOption | null;
  outliers: ReturnType<typeof useTelemetryOutliers>;
  totals: { calls: number; tokensIn: number; tokensOut: number; cost: number; errors: number };
}

function UsageBody({
  days,
  setDays,
  granularity,
  setGranularity,
  services,
  setServices,
  allServices,
  summary,
  summaryOption,
  tokensOption,
  tiers,
  tierByUser,
  tierOption,
  reanalyze,
  reanalyzeOption,
  outliers,
  totals,
}: UsageBodyProps) {
  return (
    <>
      {/* ---- Filter bar ---- */}
      <div className="mb-4 flex flex-wrap items-center gap-2">
        <div className="flex gap-1 rounded-full bg-slate-900 p-0.5 ring-1 ring-slate-800">
          {RANGES.map((r) => (
            <button
              key={r.id}
              onClick={() => setDays(r.id)}
              className={`rounded-full px-3 py-1 text-xs transition ${
                days === r.id ? "bg-slate-800 text-slate-100" : "text-slate-400 hover:text-slate-200"
              }`}
            >
              {r.label}
            </button>
          ))}
        </div>
        <div className="flex gap-1 rounded-full bg-slate-900 p-0.5 ring-1 ring-slate-800">
          {(["day", "week"] as Granularity[]).map((g) => (
            <button
              key={g}
              onClick={() => setGranularity(g)}
              className={`rounded-full px-3 py-1 text-xs capitalize transition ${
                granularity === g ? "bg-slate-800 text-slate-100" : "text-slate-400 hover:text-slate-200"
              }`}
            >
              {g}
            </button>
          ))}
        </div>
        <div className="-mx-1 flex flex-1 flex-wrap gap-1 px-1">
          {allServices.map((s) => {
            const active = services.length === 0 || services.includes(s);
            return (
              <button
                key={s}
                onClick={() => setServices(toggle(services, s))}
                className={`shrink-0 rounded-full px-2.5 py-1 text-[11px] font-medium transition ring-1 ring-inset ${
                  active
                    ? "ring-slate-700 text-slate-100"
                    : "ring-transparent text-slate-500 hover:text-slate-300"
                }`}
                style={active ? { backgroundColor: `${colorFor(s)}22`, color: colorFor(s) } : undefined}
              >
                {s}
              </button>
            );
          })}
          {services.length > 0 && (
            <button
              onClick={() => setServices([])}
              className="shrink-0 rounded-full px-2.5 py-1 text-[11px] text-slate-500 hover:text-slate-200"
            >
              clear
            </button>
          )}
        </div>
      </div>

      {/* ---- Top stats ---- */}
      <div className="mb-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
        <Stat label={`calls / ${days}d`} value={fmtCompact(totals.calls)}
              sub={totals.errors ? `${totals.errors} errors` : "no errors"} />
        <Stat label="LLM tokens in" value={fmtCompact(totals.tokensIn)} />
        <Stat label="LLM tokens out" value={fmtCompact(totals.tokensOut)} />
        <Stat label={`est. cost / ${days}d`} value={fmtUSD(totals.cost)}
              sub={totals.cost === 0 ? "free tier" : undefined} />
      </div>

      {/* ---- Calls per bucket, stacked by service ---- */}
      <div className="mb-4">
        <Card title={`Calls per ${granularity}, by service`}>
          {summary.isLoading ? (
            <div className="py-12 text-center text-sm text-slate-500">Loading…</div>
          ) : (summary.data?.points ?? []).length === 0 ? (
            <div className="py-12 text-center text-sm text-slate-500">
              No events yet — instrumentation is in place; events accrue as the cron and
              user-triggered calls fire.
            </div>
          ) : (
            <ReactECharts
              option={summaryOption}
              style={{ height: 280, width: "100%" }}
              notMerge={true}
              lazyUpdate={true}
              echarts={echarts}
            />
          )}
        </Card>
      </div>

      {/* ---- LLM tokens (in/out) ---- */}
      {tokensOption && (
        <div className="mb-4">
          <Card title="LLM tokens (prompt vs completion)">
            <ReactECharts
              option={tokensOption}
              style={{ height: 220, width: "100%" }}
              notMerge={true}
              lazyUpdate={true}
              echarts={echarts}
            />
          </Card>
        </div>
      )}

      {/* ---- Watchlist tier composition ---- */}
      <div className="mb-4">
        <Card title={`Watchlist composition (live / tracked / idle), per user · ${days}d`}>
          {tiers.isLoading ? (
            <div className="py-12 text-center text-sm text-slate-500">Loading…</div>
          ) : tierByUser.length === 0 ? (
            <div className="py-12 text-center text-sm text-slate-500">
              No tier snapshots yet. The cron writes one row per user per day at 06:05 UTC.
            </div>
          ) : (
            <div className="grid gap-4 sm:grid-cols-2">
              {tierByUser.map((u) => (
                <div key={u.uid}>
                  <div className="mb-1 text-xs text-slate-400 truncate">
                    {u.email ?? `uid:${u.uid}`}
                  </div>
                  <ReactECharts
                    option={tierOption(u.rows)}
                    style={{ height: 200, width: "100%" }}
                    notMerge={true}
                    lazyUpdate={true}
                    echarts={echarts}
                  />
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>

      {/* ---- Re-analyze usage ---- */}
      <div className="mb-4">
        <Card title="Re-analyze button — clicks per day, by user">
          {reanalyze.isLoading ? (
            <div className="py-12 text-center text-sm text-slate-500">Loading…</div>
          ) : !reanalyzeOption ? (
            <div className="py-12 text-center text-sm text-slate-500">
              No user-triggered analyses yet in the selected window.
            </div>
          ) : (
            <ReactECharts
              option={reanalyzeOption}
              style={{ height: 220, width: "100%" }}
              notMerge={true}
              lazyUpdate={true}
              echarts={echarts}
            />
          )}
        </Card>
      </div>

      {/* ---- Outliers ---- */}
      <div className="mb-4">
        <Card title={`Top users by calls · last ${Math.min(days, 30)}d`}>
          {outliers.isLoading ? (
            <div className="py-8 text-center text-sm text-slate-500">Loading…</div>
          ) : (outliers.data?.rows ?? []).length === 0 ? (
            <div className="py-8 text-center text-sm text-slate-500">
              No user-attributed events yet.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left text-xs">
                <thead className="text-[10px] uppercase tracking-wide text-slate-500">
                  <tr>
                    <th className="pb-2 pr-3">User</th>
                    <th className="pb-2 pr-3 text-right">Calls</th>
                    <th className="pb-2 pr-3 text-right">Tokens in</th>
                    <th className="pb-2 pr-3 text-right">Tokens out</th>
                    <th className="pb-2 text-right">Est. cost</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-800/60 text-slate-200">
                  {outliers.data!.rows.map((r) => (
                    <tr key={r.user_id}>
                      <td className="py-2 pr-3">{r.email ?? `uid:${r.user_id}`}</td>
                      <td className="py-2 pr-3 text-right font-mono tabular-nums">{r.calls}</td>
                      <td className="py-2 pr-3 text-right font-mono tabular-nums">{fmtCompact(r.tokens_in)}</td>
                      <td className="py-2 pr-3 text-right font-mono tabular-nums">{fmtCompact(r.tokens_out)}</td>
                      <td className="py-2 text-right font-mono tabular-nums">{fmtUSD(r.cost_micros)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      </div>

      <Footer
        note={
          <>
            Cost is computed at write time from <span className="font-mono">PRICING</span> in
            backend/app/services/telemetry.py · Re-pricing requires a backfill
          </>
        }
      />
    </>
  );
}
